import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
    AlertBannerType,
    AlertBannerVariant,
    ButtonFlavor,
    ButtonType,
    ExAlertBanner,
    ExButton,
    ExEmptyState,
    ExInput,
    ExLink,
    ExLoader,
    LinkSize,
} from '@boomi/exosphere';
import { getAllConnections, type Connection } from '../connections/connections';
import { getConnectors, type Connector } from '../connectors/connectors';
import HomeCard from './HomeCard';
import ConnectionDrawer, { type DrawerMode } from '../connections/ConnectionDrawer';
import styles from './Home.module.css';
import { GENERIC_ERROR_MESSAGE } from '../shared/apiUtils';
import { fetchIdentityProviders } from '../identity-providers/identityProviders';

const RECENT_LIMIT = 8;

const Home = () => {
    const navigate = useNavigate();
    const [connections, setConnections] = useState<Connection[]>([]);
    const [enabledIdsToApps, setEnabledIdsToApps] = useState<Record<string, string[]>>({});
    const [connectors, setConnectors] = useState<Connector[]>([]);
    const [showIDPSetup, setShowIDPSetup] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [drawerMode, setDrawerMode] = useState<DrawerMode | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [search, setSearch] = useState('');

    useEffect(() => {
        setLoading(true);
        setError(null);
        Promise.all([getAllConnections(), getConnectors(), fetchIdentityProviders()])
            .then(([connectionsData, connectorsData, identityProvidersData]) => {
                const sorted = [...connectionsData].sort(
                    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
                );
                setConnections(sorted.slice(0, RECENT_LIMIT));

                const enabledIdsToApps: Record<string, string[]> = {};
                for (const connection of connectionsData.filter((c) => c.enabled)) {
                    if (!enabledIdsToApps[connection.connectorId]) {
                        enabledIdsToApps[connection.connectorId] = [];
                    }
                    enabledIdsToApps[connection.connectorId].push(connection.appId);
                }
                setEnabledIdsToApps(enabledIdsToApps);
                setConnectors(connectorsData.sort((a, b) => a.name.localeCompare(b.name)));

                setShowIDPSetup(identityProvidersData.results.length === 0);
            })
            .catch((err) => setError(err instanceof Error ? err.message : GENERIC_ERROR_MESSAGE))
            .finally(() => setLoading(false));
    }, [refreshKey]);

    const connectorMap = new Map(connectors.map((c) => [c.id, c]));

    const filteredConnectors = search
        ? connectors.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()))
        : connectors;

    return (
        <section>
            {showIDPSetup && (
                <ExAlertBanner
                    className={styles.idpSetupAlert}
                    type={AlertBannerType.ERROR}
                    variant={AlertBannerVariant.SUBTLE}
                    open
                >
                    <div className={styles.idpSetupBody}>
                        <strong>Set IDP</strong>
                        <span className={styles.idpSetupText}>
                            One quick step: connect your identity provider to unlock all 1,100+
                            connectors.
                        </span>

                        <ExLink
                            onClick={() => navigate('/identity-providers/new')}
                            trailingIcon={'Right arrow'}
                            size={LinkSize.SMALL}
                        >
                            Set IDP
                        </ExLink>
                    </div>
                </ExAlertBanner>
            )}
            <div className={styles.recentBox}>
                <div className={styles.header}>
                    <p className={styles.recentHeading}>Recent Configs</p>
                    {connectors.length > 0 && !error && (
                        <div className={styles.actions}>
                            {connections.length > 0 && (
                                <button
                                    className={styles.viewAll}
                                    onClick={() => navigate('/connections')}
                                >
                                    View All
                                </button>
                            )}
                            <ExButton
                                type={ButtonType.PRIMARY}
                                flavor={ButtonFlavor.BRANDED}
                                onClick={() => setDrawerMode({ type: 'create' })}
                            >
                                Create config
                            </ExButton>
                        </div>
                    )}
                </div>

                {error ? (
                    <ExAlertBanner type={AlertBannerType.ERROR} open>
                        {error}
                    </ExAlertBanner>
                ) : connections.length === 0 ? (
                    loading ? (
                        <ExLoader label="Loading recent configs…" />
                    ) : (
                        <ExEmptyState
                            className={styles.noResults}
                            label="No configs configured"
                            text="Create a config to get started."
                        >
                            <div slot="header">
                                <img src="/emptyStateIcon.svg" alt="No configs configured" />
                            </div>
                        </ExEmptyState>
                    )
                ) : (
                    <div className={styles.cardRow}>
                        {connections.map((connection) => (
                            <div key={connection.appId} className={styles.cardWrap}>
                                <HomeCard
                                    name={connection.name}
                                    showCheck={connection.enabled}
                                    subtext={connection.identityProviderName}
                                    iconSrc={connectorMap.get(connection.connectorId)?.icon}
                                    onSelect={() =>
                                        setDrawerMode({
                                            type: 'edit',
                                            connectorId: connection.connectorId,
                                            connectionId: connection.appId,
                                        })
                                    }
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <ConnectionDrawer
                mode={drawerMode}
                onClose={() => {
                    setDrawerMode(null);
                    setRefreshKey((k) => k + 1);
                }}
            />
            <h2 className="ex-text-h2">All Connectors</h2>
            <ExInput
                type="search"
                placeholder="Search connectors"
                leadingIcon="magnifying-glass"
                className="ex-mb-medium"
                value={search}
                onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
            />

            {filteredConnectors.length === 0 ? (
                loading ? (
                    <ExLoader label="Loading connectors…" />
                ) : (
                    <ExEmptyState
                        className={styles.noResults}
                        label="No Results"
                        text={`No connectors found for "${search}"`}
                    >
                        <div slot="header">
                            <img src="/emptyStateIcon.svg" alt="No Results" />
                        </div>
                    </ExEmptyState>
                )
            ) : (
                <div className={styles.allConnectorsCardRow}>
                    {filteredConnectors.map((connector) => (
                        <HomeCard
                            key={connector.id}
                            name={connector.name}
                            showCheck={Object.keys(enabledIdsToApps).includes(connector.id)}
                            iconSrc={connector.icon}
                            onSelect={
                                Object.keys(enabledIdsToApps).includes(connector.id)
                                    ? () =>
                                          setDrawerMode({
                                              type: 'edit',
                                              connectorId: connector.id,
                                              connectionId: enabledIdsToApps[connector.id][0],
                                          })
                                    : () =>
                                          setDrawerMode({
                                              type: 'create',
                                              connectorId: connector.id,
                                          })
                            }
                        />
                    ))}
                </div>
            )}
        </section>
    );
};

export default Home;
